import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  EmployeeSelfServiceProvider,
  useEmployeeSelfService,
} from '../../contexts/EmployeeSelfServiceContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NotificationToast from '../../components/ui/NotificationToast';
import EmployeeSidebar from './components/EmployeeSidebar';
import MobileQuickBar from './components/MobileQuickBar';
import HomeDashboard from './components/HomeDashboard';
import AttendanceDashboard from './components/AttendanceDashboard';
import AttendanceDetails from './components/AttendanceDetails';
import LeaveSection from './components/LeaveSection';
import PayslipSection from './components/PayslipSection';
import ProfileSection from './components/ProfileSection';
import RegularizationSection from './components/RegularizationSection';
import DocumentsSection from './components/DocumentsSection';
import NotificationsSection from './components/NotificationsSection';
import PreferencesSection from './components/PreferencesSection';

const EmployeeSelfServiceInner = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const { loading, error, loadAll, notificationCenter } = useEmployeeSelfService();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toast, setToast] = useState(null);

  const notify = (payload) => setToast(payload);

  const renderContent = () => {
    const path = location.pathname;
    if (path.includes('attendance-dashboard')) return <AttendanceDashboard />;
    if (path.includes('attendance-details')) return <AttendanceDetails />;
    if (path.includes('leave')) return <LeaveSection onNotify={notify} />;
    if (path.includes('payslip')) return <PayslipSection onNotify={notify} />;
    if (path.includes('profile')) return <ProfileSection onNotify={notify} />;
    if (path.includes('regularization')) return <RegularizationSection onNotify={notify} />;
    if (path.includes('documents')) return <DocumentsSection />;
    if (path.includes('notifications')) return <NotificationsSection onNotify={notify} />;
    if (path.includes('preferences')) return <PreferencesSection onNotify={notify} />;
    return <HomeDashboard />;
  };

  const headerNotifications = notificationCenter.map((item) => ({
    id: item.id,
    title: item.title,
    message: item.message,
    type: item.type || 'info',
    timestamp: item.created_at || item.createdAt || new Date().toISOString(),
    read: !!item.read_at,
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading employee portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto text-error mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Unable to load portal</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadAll}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Employee Self-Service - LaborPro ERP</title>
        <meta name="description" content="Employee self-service portal for leave, attendance, payslips, and profile" />
      </Helmet>

      <HeaderNavigation
        user={userProfile}
        notifications={headerNotifications}
        showSidebarToggle
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="flex pt-16">
        <EmployeeSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'} overflow-x-hidden`}>
          <div className="max-w-7xl mx-auto p-4 md:p-6 w-full">
            {toast && (
              <NotificationToast
                message={toast.message}
                type={toast.type}
                onDismiss={() => setToast(null)}
                position="top-right"
                autoHideDuration={3000}
              />
            )}
            {renderContent()}
          </div>
        </main>
      </div>

      <MobileQuickBar />
    </div>
  );
};

const EmployeeSelfService = () => (
  <EmployeeSelfServiceProvider>
    <EmployeeSelfServiceInner />
  </EmployeeSelfServiceProvider>
);

export default EmployeeSelfService;
